import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  HomeIcon as HomeOutline,
  CheckBadgeIcon as CheckBadgeOutline,
  BuildingLibraryIcon as BuildingLibraryOutline,
  ArrowPathIcon as ArrowPathOutline,
  DocumentTextIcon as DocumentTextOutline
} from '@heroicons/react/24/outline';

import {
  HomeIcon as HomeSolid,
  CheckBadgeIcon as CheckBadgeSolid,
  BuildingLibraryIcon as BuildingLibrarySolid,
  ArrowPathIcon as ArrowPathSolid,
  DocumentTextIcon as DocumentTextSolid
} from '@heroicons/react/24/solid';
import SEO from '../SEO/SEO';


const HeroSection = () => {
  const { t, i18n } = useTranslation();

  // Fallback slides data (defined before state to be used as initial value)
  const fallbackSlides = [
    {
      id: 1,
      title: t('hero.slide1.title'),
      subtitle: t('hero.slide1.subtitle'),
      primary_button_text: t('hero.slide1.cta'),
      secondary_button_text: 'EMI Calculator',
      image_url: null,
      backgroundImage: 'img/Home_banner/Slide-02.webp',
      primary_button_link: "https://aham.mbwhost.in/contact",
      secondary_button_link: "#emi-calculator",
      highlights: ['No Income Proof Required', 'Quick Approval', 'Competitive Rates']
    },
    {
      id: 2,
      title: t('hero.slide2.title'),
      subtitle: t('hero.slide2.subtitle'),
      primary_button_text: t('hero.slide2.cta'),
      secondary_button_text: 'Scorecard',
      image_url: null,
      backgroundImage: 'img/Home_banner/Slide-04.webp',
      primary_button_link: "https://aham.mbwhost.in/contact",
      secondary_button_link: "#scorecard",
      highlights: ['Instant Assessment', '5-Minute Process', 'Personalized Results']
    },
    {
      id: 3,
      title: t('hero.slide3.title'),
      subtitle: t('hero.slide3.subtitle'),
      primary_button_text: t('hero.slide3.cta'),
      secondary_button_text: 'EMI Calculator',
      image_url: null,
      backgroundImage: 'img/Home_banner/Slide-03.webp',
      primary_button_link: "https://aham.mbwhost.in/pmay2",
      secondary_button_link: "#emi-calculator",
      highlights: ['Government Subsidy', 'Up to ₹1.80 Lakhs', 'PMAY Certified']
    },
    {
      id: 4,
      title: t('hero.slide4.title'),
      subtitle: t('hero.slide4.subtitle'),
      primary_button_text: t('hero.slide4.cta'),
      secondary_button_text: 'EMI Calculator',
      image_url: null,
      backgroundImage: 'img/Home_banner/Slide-05.webp',
      primary_button_link: "https://aham.mbwhost.in/e-auction",
      secondary_button_link: "#emi-calculator",
      highlights: ['Instant Calculation', 'Multiple Scenarios', 'Download Reports']
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [slides, setSlides] = useState(fallbackSlides); // Initialize with fallback data
  const [loading, setLoading] = useState(true);

  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axios.get('/api/admin/banners/active?page_type=home');
        const banners = response.data.data || [];

        // Filter for home page banners only
        const homeBanners = banners.filter(banner => banner.page_type === 'home');

        if (homeBanners.length > 0) {
          // Transform API data to match component structure
          const transformedBanners = homeBanners.map(banner => ({
            ...banner,
            highlights: typeof banner.highlights === 'string'
              ? JSON.parse(banner.highlights)
              : banner.highlights || [],
            backgroundImage: banner.image_url || 'img/Home_banner/Slide-02.webp' // fallback image
          }));
          setSlides(transformedBanners);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Slide variants for animation
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  // Determine content based on language
  const isTamil = i18n.language === 'ta';

  // Static Tamil Slides based on user requirements
  const tamilSlides = [
    {
      id: 1,
      title: t('hero.slide1.title'),
      subtitle: t('hero.slide1.subtitle'),
      primary_button_text: t('hero.slide1.cta'),
      secondary_button_text: t('hero.slide1.secondaryCta'),
      backgroundImage: 'img/Home_banner/Slide-02.webp',
      primary_button_link: "/contact",
      secondary_button_link: "#emi-calculator",
      highlights: t('hero.slide1.highlights', { returnObjects: true }) || []
    },
    {
      id: 2,
      title: t('hero.slide2.title'),
      subtitle: t('hero.slide2.subtitle'),
      primary_button_text: t('hero.slide2.cta'),
      secondary_button_text: t('hero.slide2.secondaryCta'),
      backgroundImage: 'img/Home_banner/Slide-04.webp',
      primary_button_link: "/contact",
      secondary_button_link: "#scorecard",
      highlights: t('hero.slide2.highlights', { returnObjects: true }) || []
    },
    {
      id: 3,
      title: t('hero.slide3.title'),
      subtitle: t('hero.slide3.subtitle'),
      primary_button_text: t('hero.slide3.cta'),
      secondary_button_text: t('hero.slide3.secondaryCta'),
      backgroundImage: 'img/Home_banner/Slide-03.webp',
      primary_button_link: "/pmay2",
      secondary_button_link: "#emi-calculator",
      highlights: t('hero.slide3.highlights', { returnObjects: true }) || []
    },
    {
      id: 4,
      title: t('hero.slide4.title'),
      subtitle: t('hero.slide4.subtitle'),
      primary_button_text: t('hero.slide4.cta'),
      secondary_button_text: t('hero.slide4.secondaryCta'),
      backgroundImage: 'img/Home_banner/Slide-05.webp',
      primary_button_link: "/e-auction",
      secondary_button_link: "#emi-calculator",
      highlights: t('hero.slide4.highlights', { returnObjects: true }) || []
    }
  ];

  const displaySlides = isTamil ? tamilSlides : slides;

  // Auto-advance slides every 6 seconds
  useEffect(() => {
    // Reset index if it becomes out of bounds or if slides change
    if (currentSlide >= displaySlides.length) {
      setCurrentSlide(0);
    }

    const timer = setInterval(() => {
      if (displaySlides.length > 0) {
        setDirection(1);
        setCurrentSlide((prev) => (prev + 1) % displaySlides.length);
      }
    }, 6000);
    return () => clearInterval(timer);
  }, [displaySlides.length, currentSlide]);

  // Reset index when slides change
  useEffect(() => {
    setCurrentSlide(0);
  }, [displaySlides]);

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentSlide((prev) => (prev + newDirection + displaySlides.length) % displaySlides.length);
  };

  const setSlide = (index) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  };

   // Schema for SEO
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "G129, Ground Floor, Phase 3, Spencer Plaza Mall, 786-789, Anna Salai",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600002",
          "addressCountry": "IN"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer support",
          "telephone": "+91 78239 11555",
          "email": "info@ahamhfc.com",
          "areaServed": "IN"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans at attractive rates of interest with minimum documentation or no-income-proof basis.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "G129, Ground Floor, Phase 3, Spencer Plaza Mall, 786-789, Anna Salai",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600002",
          "addressCountry": "IN"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/#webpage",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      }
    ]
  };

  
  
  return (
    <section className="relative h-[100dvh] sm:h-screen max-h-[800px] min-h-[600px] sm:min-h-[65vh] overflow-hidden bg-gray-900">
      <SEO
        title="Aham Housing Finance: Affordable Home Loan Provider in India"
        description="Apply for affordable home loans with Aham Housing Finance. Enjoy competitive rates, easy eligibility, and quick approvals. Apply now!"
        keywords="Aham Housing Finance, AHAM Housing Finance Limited, affordable home loans, home loans without income proof, no income proof home loan, low documentation home loan, home loan for self employed, home loan for informal income, customer centric home loans, transparent home loan solutions, trusted housing finance company, fast home loan approval, home purchase loan, home construction loan, home extension loan, home renovation loan, NRI home loan, balance transfer loan, mortgage loan, housing finance company India"
        canonical="https://ahamhfc.com"
        ogTitle="AHAM Housing Finance - Your Partner in Designing Dreams"
        ogDescription="Providing affordable housing finance solutions across India. Easy application, quick processing, and transparent terms."
       schema={homeSchema}
    />
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentSlide}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 }
          }}
          className="absolute inset-0 w-full h-full"
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-[75%_center] sm:bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${displaySlides[currentSlide]?.backgroundImage})`,
            }}
            role="img"
            aria-label="Aham Housing Finance corporate building and professional team providing home loan assistance in India"
          >
            <img
              src={displaySlides[currentSlide]?.backgroundImage}
              alt="Aham Housing Finance corporate building and professional team providing home loan assistance in India"
              className="hidden"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-900/80 sm:via-primary-800/80 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-primary-900/90 via-transparent to-transparent sm:hidden"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 container mx-auto px-4 sm:px-6 h-full flex items-center justify-start pb-28 pt-20 sm:pb-0 sm:pt-0">
            <div className="max-w-4xl w-full text-left">
              <div className="space-y-4 sm:space-y-6 flex flex-col items-start">
                {/* Slide Number */}
                <div className="flex items-center space-x-2 text-white/80">
                  <span className="text-xs sm:text-sm font-medium">
                    {String(currentSlide + 1).padStart(2, '0')} / {String(displaySlides.length).padStart(2, '0')}
                  </span>
                  <div className="flex space-x-1">
                    {displaySlides.map((_, index) => (
                      <div
                        key={index}
                        className={`h-1 transition-all duration-300 ${index === currentSlide ? 'w-6 sm:w-8 bg-white' : 'w-2 bg-white/40'
                          }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Main Title */}
                <motion.h1
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className={`text-[28px] sm:text-4xl md:text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight
                      ${currentSlide === 1 ? 'text-yellow-600' : 'text-white'}
                    `}
                >
                  {displaySlides[currentSlide]?.title}
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-base sm:text-lg md:text-2xl text-white/90 max-w-2xl font-medium leading-relaxed"
                >
                  {displaySlides[currentSlide]?.subtitle}
                </motion.p>

                {/* Highlights */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="flex flex-wrap justify-start gap-2 sm:gap-4"
                >
                  {(displaySlides[currentSlide]?.highlights || []).map((highlight, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-1.5 sm:space-x-2 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-lg"
                    >
                      <CheckCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-yellow-400" />
                      <span className="text-white text-[11px] sm:text-xs md:text-sm font-medium whitespace-nowrap">{highlight}</span>
                    </div>
                  ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                  className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4 w-full sm:w-auto"
                >
                  {displaySlides[currentSlide]?.primary_button_text && (
                    <a
                      href={displaySlides[currentSlide]?.primary_button_link}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-5 sm:px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[85%] sm:w-auto"
                    >
                      {displaySlides[currentSlide]?.primary_button_text}
                    </a>
                  )}
                  {displaySlides[currentSlide]?.secondary_button_text && (
                    <a
                      href={displaySlides[currentSlide]?.secondary_button_link}
                      className="bg-white text-primary-600 hover:bg-primary-50 px-5 sm:px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[85%] sm:w-auto border border-white/20"
                    >
                      {displaySlides[currentSlide]?.secondary_button_text}
                    </a>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows - Uncomment if needed */}
      {/* <button
        onClick={() => paginate(-1)}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 transform hover:scale-110"
      >
        <ChevronLeftIcon className="h-6 w-6" />
      </button>

      <button
        onClick={() => paginate(1)}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 transform hover:scale-110"
      >
        <ChevronRightIcon className="h-6 w-6" />
      </button> */}

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center space-x-6">
        {displaySlides.map((_, index) => {
          // Determine which icon to use based on index
          let IconSolid, IconOutline;
          switch (index) {
            case 0:
              IconSolid = HomeSolid;
              IconOutline = HomeOutline;
              break;
            case 1:
              IconSolid = CheckBadgeSolid;
              IconOutline = CheckBadgeOutline;
              break;
            case 2:
              IconSolid = BuildingLibrarySolid;
              IconOutline = BuildingLibraryOutline;
              break;
            case 3:
              IconSolid = ArrowPathSolid;
              IconOutline = ArrowPathOutline;
              break;
            default:
              IconSolid = DocumentTextSolid;
              IconOutline = DocumentTextOutline;
          }

          return (
            <button
              key={index}
              onClick={() => setSlide(index)}
              className={`group transition-all duration-500 focus:outline-none transform ${index === currentSlide ? 'scale-125 -translate-y-1' : 'hover:scale-110 hover:-translate-y-0.5'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            >
              {index === currentSlide ? (
                // Active: Solid Icon with glow effect
                <div className="relative">
                  <div className="absolute inset-0 bg-white/50 blur-md rounded-full transform scale-150"></div>
                  <IconSolid className="relative w-8 h-8 text-yellow-400 drop-shadow-lg" />
                </div>
              ) : (
                // Inactive: Outline Icon
                <IconOutline className="w-6 h-6 text-white/60 group-hover:text-white transition-colors duration-300" />
              )}
            </button>
          );
        })}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 right-8 z-20"
      >
        <div className="flex flex-col items-center space-y-2 text-white/80">
          <span className="text-xs font-medium uppercase tracking-wider">Scroll</span>
          <div className="w-px h-8 bg-white/60 relative">
            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute top-0 left-0 w-px h-2 bg-white"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
